import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import JobRunTicker from '../../components/job-run ticker';
import SaveBackdrop from '../../components/saveBackdrop';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import API from '../../utils/api';
import {
  apiReponseError, apiRequestError, canManualOrderSubmitter, canReadContractOrder,
  checkUserAuthenticated, formatDateForDataGrid, getShowDashboardValue, goToRoutePage,
  orderSummaries, setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import {
  AddIcon, ContractOrderIcon, DashboardOffIcon, DashboardOnIcon, MoreIcon
} from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, FormHelperText, Grid,
  Hidden, IconButton, InputLabel, Link, ListItemIcon, ListItemText, Menu, MenuItem, Select,
  TextField, Tooltip, Typography
} from '../../utils/material-ui-core';

const moment = require("moment");

const defaultChartOptionsForChargeableTypeSummary = {
  colors: ['#058DC7', '#50B432', '#ED561B', '#DDDF00', '#24CBE5', '#64E572',
    '#FF9655', '#FFF263', '#6AF9C4'],
  chart: {
    type: "column",
    style: { fontFamily: "Roboto" },
    height: "100%",
  },
  title: {
    text: "",
  },
  plotOptions: {
    column: {
      pointPadding: 0.2,
      borderWidth: 0
    }
  },
  xAxis: {
    categories: ['Chargeable Type'],
    labels: {
      autoRotation: [-45, -90]
    }
  },
  yAxis: {
    min: 0,
    title: {
      text: "Count",
    },
    lables: { enabled: true }
  },
  legend: {
    enabled: false,
  },
  series: [],
};

const defaultChartOptionsForUserSummary = {
  colors: ['#058DC7', '#50B432', '#ED561B', '#DDDF00', '#24CBE5', '#64E572', '#FF9655', '#FFF263', '#6AF9C4'],
  chart: {
    type: "column",
    style: { fontFamily: "Roboto" },
    height: "100%",
  },
  title: {
    text: "",
  },
  plotOptions: {
    column: {
      stacking: 'normal'
    }
  },
  xAxis: {
    categories: [],
  },
  yAxis: {
    allowDecimals: false,
    min: 0,
    title: {
      text: "Count",
    },

  },
  legend: {
    enabled: true,
  },
  series: [],
};

interface SearchFormInputs {
  endDate: any;
  startDate: any;
  statusId: number;
}

const ManualOrderList = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();


  const { isUserAuthenticated, referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);

  const [anchorElForOptionsMenu, setAnchorElForOptionsMenu] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [showChart, setShowChart] = useState<boolean>(false);
  const [chartOptionsForChargeableTypeSummary, setChartOptionsForChargeableTypeSummary] = useState(defaultChartOptionsForChargeableTypeSummary);
  const [chartOptionsForUserSummary, setChartOptionsForUserSummary] = useState(defaultChartOptionsForUserSummary);
  const [overallTodayStatusSummaries, setOverallTodayStatusSummaries] = useState<SummaryItem[]>([]);
  const [overallStatusSummaries, setOverallStatusSummaries] = useState<SummaryItem[]>([]);
  const [orders, setOrders] = useState([]);
  const [statusId, setStatusId] = useState<number>(1);
  const [startDate, setStartDate] = useState<Date | any>(null);
  const [endDate, setEndDate] = useState<Date | any>(null);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [filterChange, setFilterChange] = useState<boolean>(true);

  const columns = [
    { field: "status", headerName: "Status", width: 200, hide: true },
    { field: "clientCompanyName", headerName: "Client Company", width: 200 },
    { field: "programName", headerName: "Program Name", width: 200 },
    { field: "folderId", headerName: "Folder #", width: 200 },
    { field: "folderName", headerName: "Folder Name", width: 200 },
    { field: "chargeableType", headerName: "Chargeable Type", width: 200 },
    { field: "deviceOwnership", headerName: "Device Ownership", width: 200 },
    { field: "orderType", headerName: "Order Type", width: 200 },
    { field: "contractOrderNumber", headerName: "Contract #", width: 400 },
    { field: "crmCaseNumber", headerName: "CRM Case #", width: 200 },
    { field: "fulfillmentVendor", headerName: "Fulfillment Vendor", width: 200, hide: true },
    { field: "platformVendor", headerName: "Platform Vendor", width: 200, hide: true },
    { field: "installOption", headerName: "Install Option", width: 200, hide: true },
    { field: "overrideShippingAddressValidation", headerName: "Override Address", width: 200, type: "boolean" },
    { field: "shortOrderId", headerName: " Short Order #", width: 200 },
    { field: "orderId", headerName: "Order #", width: 200 },
    { field: "manualOrderApprovedBy", headerName: "Approved By", width: 250, hide: true },
    { field: "manualOrderApprovedDateTime", headerName: "Approved Date", width: 200, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "orderSubmittedDate", headerName: "Processed Date", width: 200, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 250, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 250, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "orderKey", headerName: "Order Key", width: 350, hide: true },
  ];

  // Options menu related func
  const handleAnchorElOptionsMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setAnchorElForOptionsMenu(null);
  };

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);


  const newRecord = () => {
    goToRoutePage("manualorders/" + referenceLookups.newRecordDefaultId);
  };

  const setCriteria = (id, isToday) => {

    let sdate: any = "";
    let edate: any = "";


    if (isToday) {
      sdate = moment().format("YYYY-MM-DD");
      edate = moment().format("YYYY-MM-DD");
    }

    setStatusId(Number(id));
    setStartDate(sdate);
    setEndDate(edate);
    searchCriteriaChanged();
    search2(id, sdate, edate);


  }
  const searchCriteriaChanged = () => {
    setOrders([]);
    setFilterChange(true);
  };

  const search = async () => {
    await search2(statusId, startDate, endDate);
  };

  const search2 = async (id, sdate, edate) => {
    await searchSaveOrder(id, sdate, edate);
  };

  const searchSaveOrder = async (id, sdate, edate) => {
    setOrders([]);

    setShowChart(false);

    defaultChartOptionsForChargeableTypeSummary.series = [];
    setChartOptionsForChargeableTypeSummary(defaultChartOptionsForChargeableTypeSummary);

    defaultChartOptionsForUserSummary.xAxis.categories = [];
    defaultChartOptionsForUserSummary.series = [];
    setChartOptionsForChargeableTypeSummary(defaultChartOptionsForChargeableTypeSummary);


    const payload = {
      endDate: edate !== "Invalid date" ? edate : null,
      startDate: sdate !== "Invalid date" ? sdate : null,
      statusId: id,
    };

    window.localStorage.setItem("OrderSearchForm", JSON.stringify(payload));

    setSaveInProgress(true);

    try {
      await API.post("/api/orders", payload)
        .then(async (response) => {
          const rows: any = [];
          if (response.data.orders.length > 0) {
            response.data.orders.map((item, index) =>
              rows.push({
                id: index + 1,
                ...item,
              })
            );
          }
          setOrders(rows);
          setSaveInProgress(false);
          setFilterChange(false);
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to get orders."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }

    await refreshOrderSummaries();

  };

  const refreshOrderSummaries = async () => {

    const categories: any = [];
    let series: any = [];

    defaultChartOptionsForUserSummary.xAxis.categories = categories;
    defaultChartOptionsForUserSummary.series = series;

    const apiResponse = await orderSummaries();

    if (apiResponse.success) {

      try {
        setOverallTodayStatusSummaries(apiResponse.data.orderSummaries.overallTodayStatusSummaries);
        setOverallStatusSummaries(apiResponse.data.orderSummaries.overallStatusSummaries);


        apiResponse.data.orderSummaries.userSummaries.forEach(item => {
          categories.push(item.username);
        });

        series.push({ name: "In Process", data: [], stack: "" });
        series.push({ name: "Create Draft Only", data: [], stack: "" });
        series.push({ name: "Approval Only", data: [], stack: "" });
        series.push({ name: "Create Draft & Approval", data: [], stack: "" });

        apiResponse.data.orderSummaries.userSummaries.forEach((item, index) => {
          series[0].data.push(item.inProcess);
          series[1].data.push(item.createdDraftOrder);
          series[2].data.push(item.approvedOrder);
          series[3].data.push(item.createdDraftAndApprovedOrder);
        });
      }
      catch { }

      defaultChartOptionsForUserSummary.xAxis.categories = categories;
      defaultChartOptionsForUserSummary.series = series;
      setChartOptionsForUserSummary(defaultChartOptionsForUserSummary);

      series = []
      defaultChartOptionsForChargeableTypeSummary.series = series;

      try {
        apiResponse.data.orderSummaries.chargeableTypeSummaries.forEach((item, index) => {
          series.push({
            "name": item.title,
            "data": [item.count],
            dataLabels: {
              enabled: true,
              color: '#666',
              align: 'center',
              format: '{point.y:.0f}', // one decimal
              style: {
                fontSize: '1em', fontFamily: 'roboto'
              }
            }
          });
        });
      }
      catch { }

      defaultChartOptionsForChargeableTypeSummary.series = series;
      setChartOptionsForChargeableTypeSummary(defaultChartOptionsForChargeableTypeSummary);

    }

    setShowChart(true);
  }

  const handleDataGridClick = (params: GridRowParams, event: any) => {
    goToRoutePage("manualorders/" + params.row.orderKey);
  };

  useEffect(() => {
    setWindowTitle("Order");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canManualOrderSubmitter(userFunctionAccess)) {
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("Home");
      }

      setShowDashboard(getShowDashboardValue("ShowOrderDashboard", true));

      setLoading(false);

      try {
        const tempOrderSearchFormString = window.localStorage.getItem("OrderSearchForm");

        if (tempOrderSearchFormString !== null) {
          const tempOrderSearchForm = JSON.parse(tempOrderSearchFormString) as SearchFormInputs;

          setStatusId(tempOrderSearchForm.statusId);
          setStartDate(tempOrderSearchForm.startDate);
          setEndDate(tempOrderSearchForm.endDate);

          await searchSaveOrder(tempOrderSearchForm.statusId, tempOrderSearchForm.startDate, tempOrderSearchForm.endDate);
        } else {
          setLoading(false);
        }
      } catch { }



    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Manual Orders</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Hidden xsDown>
                <Box display="flex" flexDirection="row-reverse" >
                  <IconButton onClick={() => newRecord()}>
                    <Tooltip arrow title={"Add new order"}>
                      <AddIcon color="secondary" />
                    </Tooltip>
                  </IconButton>

                  {
                    canReadContractOrder(userFunctionAccess) ?
                      <IconButton onClick={() => goToRoutePage('contractorders')}>
                        <Tooltip arrow title={"Contract Orders"}>
                          <ContractOrderIcon color="secondary" />
                        </Tooltip>
                      </IconButton>
                      : null
                  }

                  <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowOrderDashboard", showDashboard))}>
                    <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                      {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                    </Tooltip>
                  </IconButton>
                </Box>
              </Hidden>

              <Hidden smUp>
                <Box display="flex" justifyContent="flex-end" width={1}>
                  <Tooltip arrow title="Menu">
                    <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleAnchorElOptionsMenuClick}>
                      <MoreIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Hidden>

              <Menu elevation={2} id="grid-menu" anchorEl={anchorElForOptionsMenu} keepMounted open={Boolean(anchorElForOptionsMenu)} onClose={handleAnchorElOptionsMenuClose}>

                <StyledMenuItem
                  onClick={(e) => {
                    setShowDashboard(toggleShowDashboard("ShowPreOrderDashboard", showDashboard));
                    handleAnchorElOptionsMenuClose();
                  }}
                >
                  <ListItemIcon>
                    {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                </StyledMenuItem>

                {
                  canReadContractOrder(userFunctionAccess) ?
                    <StyledMenuItem
                      onClick={(e) => {
                        goToRoutePage('contractorders');
                        handleAnchorElOptionsMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <ContractOrderIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Contract Orders" />
                    </StyledMenuItem>
                    : null
                }

                <StyledMenuItem
                  onClick={(e) => {
                    newRecord();
                    handleAnchorElOptionsMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Add new order" />
                </StyledMenuItem>

              </Menu>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={showDashboard ? 8 : 12} lg={showDashboard ? 9 : 12}>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={4} lg={3} >
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Order Status</InputLabel>
                    <Select
                      required
                      native
                      labelId="order-status-label"
                      id="statusId"
                      name="statusId"
                      value={statusId}
                      onChange={(e) => {
                        if (e !== null) {
                          setStatusId(Number(e.target.value));
                        }
                        searchCriteriaChanged();
                      }}
                      label="Order Status"
                    >
                      <option value="0"></option>
                      <option value="1">Draft Order</option>
                      <option value="10">Staging</option>
                      <option value="15">Pending Install Date</option>
                      <option value="16">Confirmed Install Date</option>
                      <option value="20">Manual Contract Assignment</option>
                      <option value="30">In Queue</option>
                      <option value="75">On Hold</option>
                      <option value="80">Cancelled</option>
                      <option value="85">Cancelled with OptOut</option>
                      <option value="90">Processed OptOut</option>
                      <option value="95">Critical Error</option>
                      <option value="100">Processed</option>
                    </Select>

                    <FormHelperText error>{statusId === 0 ? "Order status is required." : ""}</FormHelperText>

                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={6} md={4} lg={3}  >
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <TextField
                      type="date"
                      id="startDate"
                      name="startDate"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(null);
                        if (e !== null) {
                          setStartDate(moment(e.target.value).format("YYYY-MM-DD"));
                        }
                        searchCriteriaChanged();
                      }}
                    />
                    <FormHelperText error>{(startDate === null || startDate === "" || startDate === "Invalid date") && endDate !== null && endDate !== "" && endDate !== "Invalid date" ? "Start date is required." : startDate !== "Invalid date" && endDate !== "Invalid date" && startDate > endDate ? "Start date must be less than or equal to end date." : ""}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={6} md={4} lg={3} >
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <TextField
                      type="date"
                      id="endDate"
                      name="endDate"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(null);
                        if (e !== null) {
                          setEndDate(moment(e.target.value).format("YYYY-MM-DD"));
                          if (startDate === null || startDate === "" || startDate === "Invalid date") {
                            setStartDate(moment(e.target.value).format("YYYY-MM-DD"));
                          }
                        }
                        searchCriteriaChanged();
                      }}
                    />
                    <FormHelperText error> </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={3} >
                  <Box p={1} display="flex" justifyContent="center">
                    {
                      statusId > 0 ?
                        <Button id="button-search" variant="contained" color="secondary" className={globalStyles.buttonDefault} disabled={loading} onClick={() => search()}>
                          {loading ? "Searching..." : "Search"}
                        </Button>
                        : null
                    }

                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box className={pageStyles.datagridContainer}>
                    {
                      !filterChange ?
                        <CustomDataGrid uniqueGridCode={"OrdersResult"} columns={columns} rows={orders} showToolBar={true} sortModel={[{ field: "lastModifiedDateTime", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
                        : null
                    }
                  </Box>
                </Grid>
              </Grid>

            </Grid>
            {
              showDashboard ?
                <Grid item xs={12} sm={12} md={4} lg={3}>

                  {
                    showChart ?
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <JobRunTicker interval={30} unit={'minutes'} mode={0} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="TODAY ORDER SUMMARY" />
                            <CardContent>
                              <Grid container spacing={3}>
                                {
                                  overallTodayStatusSummaries.length === 0 ?
                                    <Grid item xs={12}>
                                      <Box textAlign="center">
                                        <Typography variant="h6" className="count"  >
                                          No Activity Yet
                                        </Typography>
                                      </Box>

                                    </Grid>
                                    :
                                    <></>
                                }
                                {
                                  overallTodayStatusSummaries.map((summary, index) => (
                                    <Grid item key={index} xs={4}  >
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" onClick={() => { setCriteria(summary.id, false) }} >
                                        <Box>
                                          <Typography variant="h6" className="count"  >
                                            {summary.count}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            <u>{summary.title}</u>
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  ))
                                }
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="OVERALL ORDER SUMMARY" />
                            <CardContent>
                              <Grid container spacing={3}>
                                {
                                  overallStatusSummaries.map((summary, index) => (
                                    <Grid item key={index} xs={4} >
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" onClick={() => { setCriteria(summary.id, false) }} >
                                        <Box>
                                          <Typography variant="h6" className="count"  >
                                            {summary.count}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            <u>{summary.title}</u>
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  ))
                                }
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="USERS DASHBOARD" />
                            <CardContent  >
                              <HighchartsReact highcharts={Highcharts} options={chartOptionsForUserSummary} />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="CHARGEABLE TYPE DASHBOARD" />
                            <CardContent >
                              <HighchartsReact highcharts={Highcharts} options={chartOptionsForChargeableTypeSummary} />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                      : null
                  }

                </Grid>
                : null
            }
          </Grid>


        </Screen>
      )
      }
    </React.Fragment >
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 330px)",
      width: "100%",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        minHeight: "550px",
      },
    },
    countContainer: {
      justifyItems: "center",
      cursor: "default",
    },
  })
);

export default ManualOrderList;
